import { useNavigate } from "react-router-dom";
import { BarChart2, Archive, Bot, FileText, Target, Map, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useState } from "react";

const HomePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'artifacts' | 'score'>('artifacts');

  const navigateTo = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Hero Section with clear value proposition */}
        <section className="mb-6 sm:mb-8 md:mb-12 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary px-2">
            Transform Your Career Value
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-text-secondary mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto px-3 leading-relaxed">
            Document your impact, quantify your expertise, and prove your professional worth
          </p>
        </section>

        {/* Tabbed Content for Main Features */}
        <section className="mb-6 sm:mb-8 animate-fade-in">
          <div className="flex border-b border-border mb-4 sm:mb-6 overflow-x-auto">
            <button
              className={`px-4 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-base md:text-lg whitespace-nowrap flex-shrink-0 ${activeTab === 'artifacts' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
              onClick={() => setActiveTab('artifacts')}
            >
              Career Artifacts
            </button>
            <button
              className={`px-4 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-base md:text-lg whitespace-nowrap flex-shrink-0 ${activeTab === 'score' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'}`}
              onClick={() => setActiveTab('score')}
            >
              Loaded Score™
            </button>
          </div>

          {activeTab === 'artifacts' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5 mb-6 sm:mb-8">
              <Card 
                className="p-3 sm:p-4 md:p-5 bg-gradient-to-br from-accent-blue/10 to-accent-blue/20 hover:from-accent-blue/20 hover:to-accent-blue/30 transition-all duration-200 cursor-pointer border-0 touch-target-min"
                onClick={() => navigateTo("/artifacts/big-sheet")}
              >
                <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-accent-blue/20 flex-shrink-0">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-accent-blue" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-accent-blue font-semibold mb-1 text-sm sm:text-base">Big Sheet</h3>
                    <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Map your career superpowers and achievements</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full justify-start text-accent-blue hover:text-accent-blue hover:bg-accent-blue/10 p-1.5 sm:p-2 text-xs sm:text-sm">
                  Start Building
                </Button>
              </Card>
              
              <Card 
                className="p-3 sm:p-4 md:p-5 bg-gradient-to-br from-primary/10 to-primary/20 hover:from-primary/20 hover:to-primary/30 transition-all duration-200 cursor-pointer border-0 touch-target-min"
                onClick={() => navigateTo("/artifacts/power-moves")}
              >
                <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-primary/20 flex-shrink-0">
                    <Map className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-primary font-semibold mb-1 text-sm sm:text-base">Power Moves</h3>
                    <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Plan and execute career-defining strategic plays</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full justify-start text-primary hover:text-primary hover:bg-primary/10 p-1.5 sm:p-2 text-xs sm:text-sm">
                  Make Your Move
                </Button>
              </Card>
              
              <Card 
                className="p-3 sm:p-4 md:p-5 bg-gradient-to-br from-success/10 to-success/20 hover:from-success/20 hover:to-success/30 transition-all duration-200 cursor-pointer border-0 touch-target-min"
                onClick={() => navigateTo("/artifacts/okrs")}
              >
                <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-success/20 flex-shrink-0">
                    <Target className="h-4 w-4 sm:h-5 sm:w-5 text-success" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-success font-semibold mb-1 text-sm sm:text-base">OKRs</h3>
                    <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Track measurable wins and quantify your impact</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full justify-start text-success hover:text-success hover:bg-success/10 p-1.5 sm:p-2 text-xs sm:text-sm">
                  Set Objectives
                </Button>
              </Card>

              <Card 
                className="p-3 sm:p-4 md:p-5 bg-gradient-to-br from-warning/10 to-warning/20 hover:from-warning/20 hover:to-warning/30 transition-all duration-200 cursor-pointer border-0 touch-target-min"
                onClick={() => navigateTo("/artifacts/opportunity-audit")}
              >
                <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-warning/20 flex-shrink-0">
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-warning" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-warning font-semibold mb-1 text-sm sm:text-base">Opportunity Audit</h3>
                    <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">Make data-driven career decisions</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full justify-start text-warning hover:text-warning hover:bg-warning/10 p-1.5 sm:p-2 text-xs sm:text-sm">
                  Analyze Options
                </Button>
              </Card>
            </div>
          )}

          {activeTab === 'score' && (
            <div className="grid grid-cols-1 gap-6 mb-8">
              <Card className="p-6 border-0 bg-gradient-to-br from-primary/5 to-secondary/5">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="relative w-32 h-32 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full bg-primary/10"></div>
                    <div className="absolute inset-0 rounded-full bg-primary/20"></div>
                    <div className="absolute inset-2 rounded-full bg-card flex items-center justify-center flex-col">
                      <span className="text-4xl font-bold text-primary">120</span>
                      <span className="text-xs text-muted-foreground mt-1">max score</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-4 text-center md:text-left">
                    <h3 className="text-xl font-medium">The Loaded Score™</h3>
                    <p className="text-text-secondary">
                      Your comprehensive career metric that quantifies your professional impact across five key dimensions:
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span>Technical Capability</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span>Industry Expertise</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span>Leadership Impact</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span>Communication</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span>Growth Trajectory</span>
                      </li>
                    </ul>
                    <Button 
                      className="mt-4 bg-primary hover:bg-primary-hover"
                      onClick={() => navigateTo("/score")}
                    >
                      Calculate Your Score
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </section>

        {/* Secondary Buttons for AI and Dashboard */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card 
            className="p-4 sm:p-5 bg-card-bg hover:bg-card-hover transition-all duration-200 cursor-pointer border-0 touch-target-min"
            onClick={() => navigateTo("/workspace")}
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-full bg-primary/10 flex-shrink-0">
                <Bot className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-medium mb-1">AI Workspace</h3>
                <p className="text-text-secondary text-xs sm:text-sm">Get guidance from your AI sidekick</p>
              </div>
            </div>
          </Card>

          <Card 
            className="p-4 sm:p-5 bg-card-bg hover:bg-card-hover transition-all duration-200 cursor-pointer border-0 touch-target-min"
            onClick={() => navigateTo("/dashboard")}
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-full bg-secondary/10 flex-shrink-0">
                <BarChart2 className="h-4 w-4 sm:h-5 sm:w-5 text-secondary" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-medium mb-1">Dashboard</h3>
                <p className="text-text-secondary text-xs sm:text-sm">View your progress and achievements</p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-br from-primary/10 to-secondary/10 p-4 sm:p-6 rounded-xl animate-fade-in">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4">Ready to transform your career?</h2>
          <p className="text-text-secondary mb-4 sm:mb-6 text-xs sm:text-sm md:text-base leading-relaxed px-2">Join thousands of professionals who use Loaded to prove their impact and accelerate their success</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button 
              variant="default"
              size="sm"
              className="w-full sm:w-auto touch-target-min" 
              onClick={() => navigateTo("/score")}
            >
              Get Your Score
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full sm:w-auto touch-target-min"
              onClick={() => navigateTo("/artifacts")}
            >
              Build Your Portfolio
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
